import React, { useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Trash2, Save, Upload, X, BedDouble, ImageIcon, Users, IndianRupee } from 'lucide-react';
import { Button } from '../ui/button';
import { compressManyImages } from '../../utils/imageCompress';
import { ROOM_AMENITIES } from '../../data/hospitalityLists';
import { HourlyPricingEditor } from '../HourlyPricingEditor';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COMMON_AMENITIES = ROOM_AMENITIES;

const SectionTitle = ({ title, subtitle, action }) => (
  <div className="flex items-start justify-between gap-4 mb-6">
    <div>
      <h2 className="text-2xl sm:text-3xl font-black tracking-tight text-black">{title}</h2>
      {subtitle && <p className="text-sm text-gray-600 mt-1">{subtitle}</p>}
    </div>
    {action}
  </div>
);

const emptyRoom = () => ({
  _id: null,
  name: '',
  description: '',
  base_price: 1500,
  capacity: 2,
  total_rooms: 1,
  amenities: ['Air Conditioning', 'Free WiFi'],
  images: [],
  extra_adult_price: 0,
  extra_child_price: 0,
  hourly_pricing_enabled: false,
  hourly_slots: [],
});

/**
 * Hotel-Admin Rooms CRUD.
 * Props:
 *  - rooms: [{_id, name, ...}]
 *  - reloadRooms: () => Promise<void>
 */
export const RoomsSection = ({ rooms, reloadRooms }) => {
  const [editing, setEditing] = useState(null); // null | room object
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const open = (room) => setEditing(room ? { ...room, amenities: room.amenities || [], images: room.images || [] } : emptyRoom());
  const close = () => setEditing(null);

  const toggleAmenity = (a) => {
    setEditing((prev) => ({
      ...prev,
      amenities: prev.amenities.includes(a) ? prev.amenities.filter((x) => x !== a) : [...prev.amenities, a],
    }));
  };

  const handleUpload = useCallback(async (files) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    try {
      const compressed = await compressManyImages(Array.from(files), { targetKb: 200 });
      const urls = compressed.map((c) => c.dataUrl).filter(Boolean);
      setEditing((prev) => ({ ...prev, images: [...(prev.images || []), ...urls] }));
      toast.success(`${urls.length} photo${urls.length === 1 ? '' : 's'} added`);
    } catch (e) {
      toast.error('Could not process images');
    } finally {
      setUploading(false);
    }
  }, []);

  const removeImage = (idx) => {
    setEditing((prev) => ({ ...prev, images: prev.images.filter((_, i) => i !== idx) }));
  };

  const save = async () => {
    if (!editing) return;
    const body = {
      name: editing.name.trim(),
      description: (editing.description || '').trim(),
      base_price: Number(editing.base_price) || 0,
      capacity: Number(editing.capacity) || 1,
      total_rooms: Number(editing.total_rooms) || 1,
      amenities: editing.amenities,
      images: editing.images,
      extra_adult_price: Number(editing.extra_adult_price) || 0,
      extra_child_price: Number(editing.extra_child_price) || 0,
      hourly_pricing_enabled: !!editing.hourly_pricing_enabled,
      hourly_slots: editing.hourly_slots || [],
    };
    if (!body.name) return toast.error('Room name is required');
    if (body.base_price <= 0) return toast.error('Base price must be positive');
    if (body.capacity <= 0 || body.capacity > 12) return toast.error('Capacity must be between 1 and 12');
    if (body.total_rooms <= 0 || body.total_rooms > 500) return toast.error('Total rooms must be between 1 and 500');

    setSaving(true);
    try {
      if (editing._id) {
        await axios.put(`${API}/hotel/room-types/${editing._id}`, body, { withCredentials: true });
        toast.success('Room type updated');
      } else {
        await axios.post(`${API}/hotel/room-types`, body, { withCredentials: true });
        toast.success('Room type created');
      }
      close();
      await reloadRooms();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const del = async (room) => {
    if (!window.confirm(`Delete "${room.name}"? Inventory overrides will also be removed.`)) return;
    try {
      await axios.delete(`${API}/hotel/room-types/${room._id}`, { withCredentials: true });
      toast.success('Room type deleted');
      await reloadRooms();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    }
  };

  return (
    <div data-testid="rooms-section">
      <SectionTitle
        title="Rooms"
        subtitle="Add or edit room types, prices, capacity and photos. Each photo is compressed to ~200kb before upload."
        action={
          <Button
            onClick={() => open(null)}
            className="bg-[#FF0059] hover:bg-[#CC0047] text-white font-bold rounded-full"
            data-testid="rooms-add-btn"
          >
            <Plus className="h-4 w-4 mr-1" /> Add room type
          </Button>
        }
      />

      {/* ====== Photo-Verified boost banner ====== */}
      {rooms.length > 0 && (() => {
        const verified = rooms.filter((r) => (r.images || []).length >= 3).length;
        const total = rooms.length;
        if (verified === total) {
          return (
            <div className="mb-5 rounded-2xl p-4 border-2 border-[#FF0059]/30 bg-gradient-to-r from-[#FFE5EE] to-white flex items-center gap-3" data-testid="photo-boost-banner-active">
              <div className="w-10 h-10 rounded-full bg-[#FF0059] text-white flex items-center justify-center font-black flex-shrink-0">★</div>
              <div className="flex-1">
                <p className="font-black text-black text-sm">Photo-Verified · Search boost active</p>
                <p className="text-xs text-gray-600">All {total} room type{total === 1 ? '' : 's'} have 3+ photos. Your listing ranks +5% higher in search results.</p>
              </div>
            </div>
          );
        }
        const pending = total - verified;
        return (
          <div className="mb-5 rounded-2xl p-4 border-2 border-[#FFCB00] bg-[#FFF9DB] flex items-center gap-3" data-testid="photo-boost-banner-pending">
            <div className="w-10 h-10 rounded-full bg-[#FFCB00] text-black flex items-center justify-center font-black flex-shrink-0">!</div>
            <div className="flex-1">
              <p className="font-black text-black text-sm">Unlock +5% search boost</p>
              <p className="text-xs text-gray-700">
                Add 3+ photos to <span className="font-black">{pending}</span> more room type{pending === 1 ? '' : 's'} to earn the
                <span className="font-black"> Photo-Verified</span> badge and rank higher in city search.
              </p>
            </div>
          </div>
        );
      })()}

      {/* List */}
      {rooms.length === 0 ? (
        <div className="bg-white border border-dashed border-gray-300 rounded-2xl p-10 text-center" data-testid="rooms-empty">
          <BedDouble className="h-10 w-10 text-gray-300 mx-auto mb-2" />
          <p className="text-sm text-gray-600">No room types yet.</p>
          <Button onClick={() => open(null)} className="mt-3 bg-[#FF0059] hover:bg-[#CC0047] text-white font-bold rounded-full" data-testid="rooms-empty-add-btn">
            <Plus className="h-4 w-4 mr-1" /> Create your first room
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="rooms-list">
          {rooms.map((r) => {
            const cover = (Array.isArray(r.images) && r.images[0]) || null;
            return (
              <div key={r._id} className="bg-white rounded-2xl border border-gray-200 overflow-hidden flex flex-col" data-testid={`room-card-${r._id}`}>
                <div className="aspect-video bg-gray-100">
                  {cover ? (
                    <img src={cover} alt={r.name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-gray-400 text-xs">No photo</div>
                  )}
                </div>
                <div className="p-4 flex-1 flex flex-col">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <h3 className="font-black text-base text-black truncate">{r.name}</h3>
                      <p className="text-xs text-gray-500 line-clamp-2 mt-0.5">{r.description}</p>
                    </div>
                    <p className="text-lg font-black text-black whitespace-nowrap">₹{Number(r.base_price || 0).toLocaleString('en-IN')}</p>
                  </div>
                  <div className="flex flex-wrap gap-3 mt-3 text-xs text-gray-600">
                    <span className="flex items-center gap-1"><Users className="h-3 w-3" /> Capacity {r.capacity}</span>
                    <span className="flex items-center gap-1"><BedDouble className="h-3 w-3" /> {r.total_rooms || 1} room{(r.total_rooms || 1) === 1 ? '' : 's'}</span>
                    <span className="flex items-center gap-1"><ImageIcon className="h-3 w-3" /> {(r.images || []).length} photo{(r.images || []).length === 1 ? '' : 's'}</span>
                  </div>
                  <div className="mt-auto pt-4 flex gap-2">
                    <Button onClick={() => open(r)} variant="outline" className="flex-1 font-bold rounded-full" data-testid={`room-edit-${r._id}`}>
                      Edit
                    </Button>
                    <Button onClick={() => del(r)} variant="outline" className="font-bold rounded-full border-red-200 text-red-600 hover:bg-red-50 hover:border-red-300" data-testid={`room-delete-${r._id}`}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ============ Edit / Create modal ============ */}
      {editing && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={close} data-testid="room-modal-overlay">
          <div
            className="bg-white rounded-3xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6 sm:p-8"
            onClick={(e) => e.stopPropagation()}
            data-testid="room-modal"
          >
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-2xl font-black text-black">{editing._id ? 'Edit room type' : 'Add room type'}</h3>
                <p className="text-xs text-gray-500 mt-0.5">Changes go live immediately on your listing.</p>
              </div>
              <button onClick={close} className="p-2 rounded-full hover:bg-gray-100" aria-label="Close" data-testid="room-modal-close">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-4">
              <Field label="Room name" required>
                <input
                  type="text"
                  value={editing.name}
                  onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                  placeholder="e.g. Deluxe King Room"
                  className="w-full px-3 py-2 rounded-xl border border-gray-200 focus:border-[#FF0059] outline-none text-sm font-medium"
                  data-testid="room-name-input"
                />
              </Field>

              <Field label="Description">
                <textarea
                  rows={3}
                  value={editing.description}
                  onChange={(e) => setEditing({ ...editing, description: e.target.value })}
                  placeholder="A short description shown on the listing"
                  className="w-full px-3 py-2 rounded-xl border border-gray-200 focus:border-[#FF0059] outline-none text-sm"
                  data-testid="room-description-input"
                />
              </Field>

              <div className="grid grid-cols-3 gap-3">
                <Field label="Base ₹/night" required>
                  <div className="relative">
                    <IndianRupee className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
                    <input
                      type="number"
                      min="100"
                      step="50"
                      value={editing.base_price}
                      onChange={(e) => setEditing({ ...editing, base_price: e.target.value })}
                      className="w-full pl-8 pr-3 py-2 rounded-xl border border-gray-200 focus:border-[#FF0059] outline-none text-sm font-bold"
                      data-testid="room-price-input"
                    />
                  </div>
                </Field>
                <Field label="Capacity">
                  <input
                    type="number"
                    min="1"
                    max="12"
                    value={editing.capacity}
                    onChange={(e) => setEditing({ ...editing, capacity: e.target.value })}
                    className="w-full px-3 py-2 rounded-xl border border-gray-200 focus:border-[#FF0059] outline-none text-sm font-bold"
                    data-testid="room-capacity-input"
                  />
                </Field>
                <Field label="Total rooms">
                  <input
                    type="number"
                    min="1"
                    max="500"
                    value={editing.total_rooms}
                    onChange={(e) => setEditing({ ...editing, total_rooms: e.target.value })}
                    className="w-full px-3 py-2 rounded-xl border border-gray-200 focus:border-[#FF0059] outline-none text-sm font-bold"
                    data-testid="room-total-input"
                  />
                </Field>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <Field label="Extra adult (₹/night)">
                  <input
                    type="number"
                    min="0"
                    step="50"
                    value={editing.extra_adult_price ?? 0}
                    onChange={(e) => setEditing({ ...editing, extra_adult_price: e.target.value })}
                    className="w-full px-3 py-2 rounded-xl border border-gray-200 focus:border-[#FF0059] outline-none text-sm font-bold"
                    data-testid="room-extra-adult-input"
                  />
                </Field>
                <Field label="Extra child (₹/night)">
                  <input
                    type="number"
                    min="0"
                    step="50"
                    value={editing.extra_child_price ?? 0}
                    onChange={(e) => setEditing({ ...editing, extra_child_price: e.target.value })}
                    className="w-full px-3 py-2 rounded-xl border border-gray-200 focus:border-[#FF0059] outline-none text-sm font-bold"
                    data-testid="room-extra-child-input"
                  />
                </Field>
              </div>

              <HourlyPricingEditor
                enabled={!!editing.hourly_pricing_enabled}
                slots={editing.hourly_slots || []}
                onChange={({ enabled: en, slots: sl }) => setEditing({ ...editing, hourly_pricing_enabled: en, hourly_slots: sl })}
                testIdPrefix="hourly"
              />

              <Field label="Amenities">
                <div className="flex flex-wrap gap-2" data-testid="room-amenities-row">
                  {COMMON_AMENITIES.map((a) => {
                    const on = editing.amenities.includes(a);
                    return (
                      <button
                        key={a}
                        type="button"
                        onClick={() => toggleAmenity(a)}
                        className={`px-3 py-1.5 rounded-full text-xs font-bold border-2 transition-colors ${on ? 'bg-[#FF0059] text-white border-[#FF0059]' : 'bg-white text-gray-700 border-gray-200 hover:border-[#FF0059]'}`}
                        data-testid={`room-amenity-${a.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '')}`}
                      >
                        {a}
                      </button>
                    );
                  })}
                </div>
              </Field>

              <Field label={`Photos (${editing.images.length})`}>
                <div className="space-y-2">
                  <label
                    className={`flex items-center justify-center gap-2 px-4 py-3 rounded-xl border-2 border-dashed cursor-pointer transition-colors ${uploading ? 'border-gray-200 opacity-60' : 'border-gray-300 hover:border-[#FF0059]'}`}
                    data-testid="room-upload-label"
                  >
                    <Upload className="h-4 w-4" />
                    <span className="text-sm font-bold text-gray-700">
                      {uploading ? 'Compressing…' : 'Click to upload room photos'}
                    </span>
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      className="hidden"
                      onChange={(e) => { handleUpload(e.target.files); e.target.value = ''; }}
                      disabled={uploading}
                      data-testid="room-upload-input"
                    />
                  </label>
                  {editing.images.length > 0 && (
                    <div className="grid grid-cols-3 sm:grid-cols-4 gap-2" data-testid="room-photos-grid">
                      {editing.images.map((img, idx) => (
                        <div key={`${idx}-${(img || '').slice(-16)}`} className="relative group aspect-square rounded-lg overflow-hidden bg-gray-100">
                          <img src={img} alt={`Room photo ${idx + 1}`} className="w-full h-full object-cover" />
                          <button
                            type="button"
                            onClick={() => removeImage(idx)}
                            className="absolute top-1 right-1 p-1 rounded-full bg-white/90 hover:bg-red-500 hover:text-white transition-colors"
                            aria-label="Remove photo"
                            data-testid={`room-photo-remove-${idx}`}
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </Field>
            </div>

            <div className="flex gap-3 mt-6 pt-4 border-t border-gray-100">
              <Button onClick={close} variant="outline" className="flex-1 font-bold rounded-full" data-testid="room-cancel-btn">
                Cancel
              </Button>
              <Button
                onClick={save}
                disabled={saving}
                className="flex-1 bg-[#FF0059] hover:bg-[#CC0047] text-white font-bold rounded-full"
                data-testid="room-save-btn"
              >
                <Save className="h-4 w-4 mr-1" /> {saving ? 'Saving…' : 'Save'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const Field = ({ label, required, children }) => (
  <div>
    <label className="text-[11px] font-bold uppercase tracking-wider text-gray-500 block mb-1">
      {label}{required && <span className="text-[#FF0059] ml-0.5">*</span>}
    </label>
    {children}
  </div>
);
